import React, { memo } from "react";
import Modal from "@ui/Modal";
import ModalForm from "@ui/Modal/Form";
import { useDashboardEditorContext } from "../../hook/context/editor";
import useToasts from "@ui/Toast/useToasts";
import { useDashboardEditorState } from "../../hook/store/editor";

import { useNavigate } from "react-router";
import { useDashboardUnsaved } from "../../hook/useDashboardUnsaved";
import t from "../../../../../i18n";
import { useDashboardSaveCallback } from "../../hook/action/save";
import { useUnlockDashboardCallback } from "../../hook/action/lock";
import { useTranslations } from "../../../../../api/useTranslations";
import { toPath } from "../../../../../router/helpers/path";
import { useDashboard } from "../../hook/api/dashboard";

export const ModalDashboardQuitEditing = memo(
  ({
    projectId,
    dashboardId,
    opened,
    close,
  }: {
    projectId: string;
    dashboardId: string;
    opened: boolean;
    close: () => void;
  }) => {
    const translations = useTranslations();
    const navigate = useNavigate();
    const { addToast } = useToasts();
    const { dashboard } = useDashboard(projectId, dashboardId);
    const editor = useDashboardEditorContext();
    const editorState = useDashboardEditorState(editor?.store);
    const [, setUnsaved] = useDashboardUnsaved({ scope: dashboardId });

    const path = React.useMemo(
      () =>
        dashboard
          ? toPath("/projects/:projectId/dashboards/:dashboardId", {
              dashboardId: dashboard.id,
              projectId: dashboard.projectId,
            })
          : null,
      [dashboard],
    );

    const unlockDashboard = useUnlockDashboardCallback(projectId);
    const clearAndQuit = React.useCallback(async () => {
      if (!dashboard) return;
      const unlocked = await unlockDashboard(dashboard.id);
      if (!unlocked) return;
      editor?.focus?.(null);
      void editor?.clear?.();
      if (path) void navigate(path);
    }, [dashboard, editor, navigate, path, unlockDashboard]);

    const saveDashboard = useDashboardSaveCallback();
    const saveChanges = React.useCallback(() => {
      if (!editorState) return true;
      const toUpdateDashboard = {
        ...editorState.dashboard,
      };
      const toUpdateBlocks = editorState.blocks;

      return saveDashboard({
        toUpdateDashboard,
        toUpdateBlocks,
      })
        .then(() => {
          setUnsaved(false);
          addToast(
            "success",
            t("project.dashboard.actions.save.success", translations, toUpdateDashboard.published),
          );
          return true;
        })
        .catch(() => {
          addToast("error", t("project.dashboard.actions.save.error"));
          return true;
        });
    }, [addToast, editorState, saveDashboard, setUnsaved, translations]);

    const clear = React.useCallback(() => {
      close();
      setUnsaved(false);
      void clearAndQuit();
    }, [clearAndQuit, close, setUnsaved]);

    return (
      <Modal
        title={t("project.dashboard.transaction.save.title")}
        onRequestClose={close}
        isOpen={opened}
        size="l"
      >
        <ModalForm
          onSubmit={saveChanges}
          onRequestClose={clear}
          okButtonName={t("project.dashboard.transaction.save.submit_action")}
          cancelButtonName={t("project.dashboard.transaction.save.cancel_action")}
        >
          <p>{t("project.dashboard.transaction.save.subtitle", translations)}</p>
        </ModalForm>
      </Modal>
    );
  },
);
